/**
 * Client-side SSH key pair generation and OpenSSH format conversion.
 * Supports RSA (4096) and Ed25519 via the Web Crypto API.
 */

import JSZip from "jszip";

export type KeyAlgorithm = "rsa-4096" | "ed25519";

export interface KeyAlgorithmOption {
  id: KeyAlgorithm;
  label: string;
  description: string;
}

export const KEY_ALGORITHM_OPTIONS: KeyAlgorithmOption[] = [
  {
    id: "rsa-4096",
    label: "RSA",
    description: "Maximum compatibility. Uses 4096-bit key size.",
  },
  {
    id: "ed25519",
    label: "Ed25519",
    description: "Modern, fast, and compact.",
  },
];

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function encodeSSHString(data: Uint8Array): Uint8Array {
  const buf = new Uint8Array(4 + data.length);
  new DataView(buf.buffer).setUint32(0, data.length);
  buf.set(data, 4);
  return buf;
}

function encodeSSHMpint(value: Uint8Array): Uint8Array {
  let start = 0;
  while (start < value.length - 1 && value[start] === 0) start++;
  const trimmed = value.subarray(start);

  const needsPadding = trimmed[0] & 0x80;
  const len = trimmed.length + (needsPadding ? 1 : 0);
  const buf = new Uint8Array(4 + len);
  new DataView(buf.buffer).setUint32(0, len);
  if (needsPadding) {
    buf[4] = 0;
    buf.set(trimmed, 5);
  } else {
    buf.set(trimmed, 4);
  }
  return buf;
}

function concatParts(parts: Uint8Array[]): Uint8Array {
  const totalLen = parts.reduce((s, p) => s + p.length, 0);
  const result = new Uint8Array(totalLen);
  let offset = 0;
  for (const part of parts) {
    result.set(part, offset);
    offset += part.length;
  }
  return result;
}

function wrapPEM(base64: string, label: string): string {
  const lines: string[] = [];
  for (let i = 0; i < base64.length; i += 64) {
    lines.push(base64.substring(i, i + 64));
  }
  return `-----BEGIN ${label}-----\n${lines.join("\n")}\n-----END ${label}-----\n`;
}

// ─── RSA ───────────────────────────────────────────────────────────

function encodeOpenSSHRSAPublicKey(publicKey: ArrayBuffer): string {
  const spki = new Uint8Array(publicKey);
  const rsaPublicKey = parseRSAPublicKeyFromSPKI(spki);
  if (!rsaPublicKey) throw new Error("Failed to parse RSA public key from SPKI");

  const { modulus, exponent } = rsaPublicKey;
  const keyType = new TextEncoder().encode("ssh-rsa");
  const wireKey = concatParts([
    encodeSSHString(keyType),
    encodeSSHMpint(exponent),
    encodeSSHMpint(modulus),
  ]);

  return `ssh-rsa ${arrayBufferToBase64(wireKey.buffer)}`;
}

function parseRSAPublicKeyFromSPKI(
  spki: Uint8Array,
): { modulus: Uint8Array; exponent: Uint8Array } | null {
  try {
    let pos = 0;

    const readTag = () => {
      const tag = spki[pos++];
      let length = spki[pos++];
      if (length & 0x80) {
        const numBytes = length & 0x7f;
        length = 0;
        for (let i = 0; i < numBytes; i++) {
          length = (length << 8) | spki[pos++];
        }
      }
      return { tag, length };
    };

    const readInteger = (): Uint8Array => {
      const { tag, length } = readTag();
      if (tag !== 0x02) throw new Error(`Expected INTEGER (0x02), got 0x${tag.toString(16)}`);
      const value = spki.subarray(pos, pos + length);
      pos += length;
      return value;
    };

    const outer = readTag();
    if (outer.tag !== 0x30) return null;

    const algoId = readTag();
    if (algoId.tag !== 0x30) return null;
    pos += algoId.length;

    const bitString = readTag();
    if (bitString.tag !== 0x03) return null;
    pos++;

    const rsaSeq = readTag();
    if (rsaSeq.tag !== 0x30) return null;

    const modulus = readInteger();
    const exponent = readInteger();

    return { modulus, exponent };
  } catch {
    return null;
  }
}

async function generateRSA(bits: 2048 | 4096): Promise<{
  publicKeyOpenSSH: string;
  privateKeyPEM: string;
}> {
  const keyPair = await window.crypto.subtle.generateKey(
    {
      name: "RSASSA-PKCS1-v1_5",
      modulusLength: bits,
      publicExponent: new Uint8Array([0x01, 0x00, 0x01]),
      hash: "SHA-256",
    },
    true,
    ["sign", "verify"],
  );

  const spkiBuffer = await window.crypto.subtle.exportKey("spki", keyPair.publicKey);
  const pkcs8Buffer = await window.crypto.subtle.exportKey("pkcs8", keyPair.privateKey);

  const publicKeyOpenSSH = encodeOpenSSHRSAPublicKey(spkiBuffer);
  const privateKeyPEM = wrapPEM(arrayBufferToBase64(pkcs8Buffer), "PRIVATE KEY");

  return { publicKeyOpenSSH, privateKeyPEM };
}

// ─── Ed25519 ───────────────────────────────────────────────────────

function encodeOpenSSHEd25519PublicKey(rawPublicKey: Uint8Array): string {
  const keyType = new TextEncoder().encode("ssh-ed25519");
  const wireKey = concatParts([
    encodeSSHString(keyType),
    encodeSSHString(rawPublicKey),
  ]);
  return `ssh-ed25519 ${arrayBufferToBase64(wireKey.buffer)}`;
}

async function generateEd25519(): Promise<{
  publicKeyOpenSSH: string;
  privateKeyPEM: string;
}> {
  const keyPair = await window.crypto.subtle.generateKey(
    { name: "Ed25519" } as EcKeyGenParams,
    true,
    ["sign", "verify"],
  );

  const rawPublicKey = new Uint8Array(
    await window.crypto.subtle.exportKey("raw", keyPair.publicKey),
  );
  const pkcs8Buffer = await window.crypto.subtle.exportKey("pkcs8", keyPair.privateKey);

  const publicKeyOpenSSH = encodeOpenSSHEd25519PublicKey(rawPublicKey);
  const privateKeyPEM = wrapPEM(arrayBufferToBase64(pkcs8Buffer), "PRIVATE KEY");

  return { publicKeyOpenSSH, privateKeyPEM };
}

// ─── Public API ────────────────────────────────────────────────────

export async function generateKeyPair(algorithm: KeyAlgorithm = "rsa-4096"): Promise<{
  publicKeyOpenSSH: string;
  privateKeyPEM: string;
}> {
  switch (algorithm) {
    case "rsa-4096":
      return generateRSA(4096);
    case "ed25519":
      return generateEd25519();
  }
}

/** @deprecated Use generateKeyPair("rsa-4096") instead */
export const generateRSAKeyPair = () => generateKeyPair("rsa-4096");

function sshKeyBundleNames(filePrefix: string, algorithm: KeyAlgorithm) {
  const raw = (filePrefix || "sftp").trim();
  const prefix = raw.replace(/[^\w.-]/g, "_") || "sftp";
  const suffix = algorithm === "ed25519" ? "ed25519" : "rsa";
  return {
    prefix,
    suffix,
    privateName: `${prefix}_id_${suffix}`,
    publicName: `${prefix}_id_${suffix}.pub`,
    passphraseName: `${prefix}_id_${suffix}_passphrase.txt`,
    zipName: `${prefix}_ssh_keys.zip`,
  };
}

/** One .zip containing private key, .pub, and passphrase reminder .txt (names match former separate downloads). */
export async function downloadSshKeyBundleAsZip(
  privateKeyPEM: string,
  publicKeyOpenSSH: string,
  filePrefix: string,
  algorithm: KeyAlgorithm,
  passphrasePlaintext: string,
): Promise<void> {
  const { privateName, publicName, passphraseName, zipName } = sshKeyBundleNames(
    filePrefix,
    algorithm,
  );

  const zip = new JSZip();
  zip.file(
    privateName,
    privateKeyPEM.endsWith("\n") ? privateKeyPEM : `${privateKeyPEM}\n`,
  );
  zip.file(
    publicName,
    publicKeyOpenSSH.endsWith("\n") ? publicKeyOpenSSH : `${publicKeyOpenSSH}\n`,
  );
  zip.file(passphraseName, `${passphrasePlaintext}\n`);

  const blob = await zip.generateAsync({ type: "blob", compression: "DEFLATE" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = zipName;
  link.click();
  URL.revokeObjectURL(url);
}
